<script setup lang="ts">
import { PropertyType } from '@/backend/types'
import { useAgentUIStore } from '@/modules/Cases/Sidebar/useAgentUIStore'
import { injectFieldContext } from '@/modules/Project/Fields/fieldContext'
import type { NavigationParams } from '@/modules/Project/Navigation/navigationTypes'
import ProjectLink from '@/modules/Project/Navigation/ProjectLink.vue'
import { getSingleParam } from '@/shared/utils/routing'
import { isFileField } from '@/shared/utils/typeGuards'
import { useRouteParams } from '@/sharedComposables/useRouteParams'
import { buildFileField } from '@/test/factories/field'
import IconButton from '@/uiKit/IconButton.vue'
import { computed } from 'vue'
import { useRoutes } from '../App/useRoutes'
import { useEntitySidebarNavigation } from './Navigation/useEntitySidebarNavigation'

const props = defineProps<{
  viewId?: string
  filename: string | null
}>()

const emit = defineEmits<{
  (e: 'delete' | 'rename'): void
}>()

const { field, column, property } = injectFieldContext()

const showDelete = computed(() => props.filename !== null)
const showExpand = computed(() => column.value === 0)
const isCollection = computed(() => property.value.type === PropertyType.collection)

const { currentlyInSubProject, route } = useRoutes()
const { parentEntityId, parentViewId } = useRouteParams()

const isCaseDetails = computed(() => route.name === 'CaseDetails')

const navigationData = computed<NavigationParams>(() => {
  const parentProjectId = currentlyInSubProject.value
    ? getSingleParam(route.params.parentProjectId)
    : undefined

  return {
    parentProjectId,
    parentEntityId: getSingleParam(parentEntityId.value),
    viewId: props.viewId,
    entityId: field.value.entityId,
    parentViewId: getSingleParam(parentViewId.value),
  }
})

const { resolveEntitySidebarRoute } = useEntitySidebarNavigation()
const sidebarRoute = computed(() => {
  return resolveEntitySidebarRoute(
    field.value.workspaceId,
    field.value.projectId,
    field.value.entityId,
  )
})

const agentUIStore = useAgentUIStore()

function handleExpandEntity() {
  if (isFileField(field.value) && field.value.entityId) {
    // Construct a minimal CaseFile object for agentUIStore
    const file = buildFileField({
      type: 'file',
      manualFilename: field.value.manualFilename,
      toolFilename: field.value.toolFilename || null,
      manualValuePDF: null,
      toolValuePDF: null,
      propertyId: field.value.propertyId,
      entityId: field.value.entityId,
      status: field.value.status,
      staleness: field.value.staleness,
      errorMessage: field.value.errorMessage,
      propertyHash: field.value.propertyHash,
      manualValueUpdatedBy: null,
      toolValueUpdatedBy: null,
      updatedAt: null,
      createdAt: '',
      groundTruth: false,
      score: null,
      manualValue: null,
      toolValue: null,
      workspaceId: field.value.workspaceId,
      projectId: field.value.projectId,
    })
    agentUIStore.setLastPreviewedFile(file)
  }
}
</script>

<template>
  <div>
    <div
      v-if="showDelete || showExpand || isCollection"
      class="flex items-center justify-end gap-0.5 rounded-md bg-surface-primary p-0.5 text-text-subtle shadow-xs"
    >
      <ProjectLink
        v-if="isCaseDetails && showExpand"
        title="Expand entity"
        :to="navigationData"
        aria-label="Expand entity"
        data-test="entity-view-link"
        @click.stop
      >
        <IconButton
          icon="expand"
          size="sm"
          variant="transparent"
        />
      </ProjectLink>
      <IconButton
        v-else-if="!isCollection"
        icon="expand"
        size="sm"
        variant="transparent"
        :to="sidebarRoute"
        aria-label="Expand entity"
        title="Expand entity"
        @click="handleExpandEntity"
      />

      <IconButton
        v-if="showDelete"
        class="hidden group-hover/cell:flex"
        title="Delete file"
        aria-label="Delete file"
        icon="trash"
        size="sm"
        variant="transparent"
        @click.stop.prevent="emit('delete')"
        @mousedown.prevent
        @dblclick.stop
      />

      <IconButton
        v-if="isCollection"
        class="hidden group-hover/cell:flex"
        title="Rename collection"
        aria-label="Rename collection"
        icon="edit"
        size="sm"
        variant="transparent"
        @click.stop.prevent="emit('rename')"
        @mousedown.prevent
        @dblclick.stop
      />
    </div>
  </div>
</template>
